import { DialogCloseEvent } from '@theme/dialog';
import { clamp, preventDefault, isMobileBreakpoint } from '@theme/utilities';
import { Component } from '@theme/component';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const DEFAULT_ZOOM = 1;
const DOUBLE_TAP_DELAY = 300;
const DOUBLE_TAP_DISTANCE = 50;
const DRAG_THRESHOLD = 10;

/** @extends {Component<Refs>} */
export class DragZoomWrapper extends Component {
  requiredRefs = ['image'];
  #controller = new AbortController();
  #scale = DEFAULT_ZOOM;
  #initialDistance = 0;
  #startScale = DEFAULT_ZOOM;
  #translate = { x: 0, y: 0 };
  #startPosition = { x: 0, y: 0 };
  #startTranslate = { x: 0, y: 0 };
  #isDragging = false;
  #initialized = false;
  #animationFrame = null;
  #lastTapTime = 0;
  #lastTapPosition = null;
  #hasDraggedBeyondThreshold = false;
  #hasManualZoom = false;

  get #image() { return this.refs.image; }

  connectedCallback() {
    super.connectedCallback();
    if (!this.#image) return;
    this.#initResizeListener();
    window.addEventListener(DialogCloseEvent.eventName, this.#resetZoom);
    if (!isMobileBreakpoint()) return;
    this.#initEventListeners();
    this.#updateTransform();
  }

  #initResizeListener() { this.#resizeObserver.observe(this); }

  #initEventListeners() {
    if (this.#initialized) return;
    this.#initialized = true;
    const { signal } = this.#controller;
    // touchstart en passive:true para permitir swipe al slideshow
    this.addEventListener('touchstart', this.#handleTouchStart, { passive: true, signal });
    this.addEventListener('touchmove', this.#handleTouchMove, { passive: false, signal });
    this.addEventListener('touchend', this.#handleTouchEnd, { passive: false, signal });
    this.#updateTransform();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(DialogCloseEvent.eventName, this.#resetZoom);
    this.#controller.abort();
    this.#resizeObserver.disconnect();
    this.#cancelAnimationFrame();
  }

  #handleResize = () => {
    if (!this.#initialized && isMobileBreakpoint()) this.#initEventListeners();
    if (this.#initialized) this.#requestUpdateTransform();
  };

  #resizeObserver = new ResizeObserver(this.#handleResize);

  #handleTouchStart = (event) => {
    const touchCount = event.touches.length;
    if (touchCount === 2) {
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (!touch1 || !touch2) return;
      this.#startZoomGestureFromTouches(touch1, touch2);
    } else if (touchCount === 1) {
      const touch = event.touches[0];
      if (!touch) return;
      const currentTime = performance.now();
      const timeSinceLastTap = currentTime - this.#lastTapTime;
      if (timeSinceLastTap >= DOUBLE_TAP_DELAY) {
        this.#storeTapInfo(currentTime, touch);
        this.#startDragGestureFromTouch(touch);
        return;
      }
      if (this.#lastTapPosition) {
        const distance = getDistance(touch, this.#lastTapPosition);
        if (distance < DOUBLE_TAP_DISTANCE) {
          this.#handleDoubleTapFromTouch(touch);
          this.#lastTapTime = 0;
          this.#lastTapPosition = null;
          return;
        }
      }
      this.#storeTapInfo(currentTime, touch);
      this.#startDragGestureFromTouch(touch);
    }
  };

  #startZoomGestureFromTouches(touch1, touch2) {
    this.#initialDistance = getDistance(touch1, touch2);
    this.#startScale = this.#scale;
    this.#isDragging = false;
  }

  #startDragGestureFromTouch(touch) {
    this.#startPosition = { x: touch.clientX, y: touch.clientY };
    this.#startTranslate = { x: this.#translate.x, y: this.#translate.y };
    this.#isDragging = true;
    this.#hasDraggedBeyondThreshold = false;
  }

  #storeTapInfo(currentTime, touch) {
    this.#lastTapTime = currentTime;
    this.#lastTapPosition = { x: touch.clientX, y: touch.clientY };
  }

  #handleDoubleTapFromTouch(touch) {
    const containerCenter = { x: this.clientWidth / 2, y: this.clientHeight / 2 };
    let targetZoom;
    if (this.#hasManualZoom) {
      targetZoom = MIN_ZOOM;
      this.#hasManualZoom = false;
      this.#translate = { x: 0, y: 0 };
    } else {
      const tolerance = 0.05;
      targetZoom = Math.abs(this.#scale - MIN_ZOOM) < tolerance ? 2.5 : MIN_ZOOM;
    }
    if (targetZoom !== MIN_ZOOM) {
      const oldScale = this.#scale;
      this.#scale = Math.min(MAX_ZOOM, targetZoom);
      const distanceFromCenter = { x: touch.clientX - containerCenter.x, y: touch.clientY - containerCenter.y };
      const scaleDelta = this.#scale / oldScale - 1.0;
      this.#translate.x -= (distanceFromCenter.x * scaleDelta) / this.#scale;
      this.#translate.y -= (distanceFromCenter.y * scaleDelta) / this.#scale;
    } else {
      this.#scale = targetZoom;
      this.#translate = { x: 0, y: 0 };
    }
    this.#requestUpdateTransform();
  }

  #handleTouchMove = (event) => {
    const touchCount = event.touches.length;
    if (touchCount === 2) {
      preventDefault(event);
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (touch1 && touch2) this.#processZoomGesture(touch1, touch2);
    } else if (touchCount === 1 && this.#isDragging) {
      const touch = event.touches[0];
      if (!touch) return;
      if (this.#scale <= MIN_ZOOM) {
        const dx = Math.abs(touch.clientX - this.#startPosition.x);
        const dy = Math.abs(touch.clientY - this.#startPosition.y);
        if (dx > dy) return; // swipe horizontal → pasa al slideshow
      }
      preventDefault(event);
      this.#processDragGesture(touch);
    }
  };

  #processZoomGesture(touch1, touch2) {
    const midX = (touch1.clientX + touch2.clientX) / 2;
    const midY = (touch1.clientY + touch2.clientY) / 2;
    const currentDistance = getDistance(touch1, touch2);
    const oldScale = this.#scale;
    const newScale = (currentDistance / this.#initialDistance) * this.#startScale;
    this.#scale = clamp(newScale, MIN_ZOOM, MAX_ZOOM);
    this.#hasManualZoom = true;
    const containerCenterX = this.clientWidth / 2;
    const containerCenterY = this.clientHeight / 2;
    const distanceFromCenterX = midX - containerCenterX;
    const distanceFromCenterY = midY - containerCenterY;
    const scaleDelta = this.#scale / oldScale - 1.0;
    this.#translate.x -= (distanceFromCenterX * scaleDelta) / this.#scale;
    this.#translate.y -= (distanceFromCenterY * scaleDelta) / this.#scale;
    this.#requestUpdateTransform();
    this.#isDragging = false;
  }

  #processDragGesture(touch) {
    const distance = getDistance(touch, this.#startPosition);
    if (!this.#hasDraggedBeyondThreshold && distance < DRAG_THRESHOLD) return;
    this.#hasDraggedBeyondThreshold = true;
    const dx = touch.clientX - this.#startPosition.x;
    const dy = touch.clientY - this.#startPosition.y;
    this.#translate.x = this.#startTranslate.x + dx / this.#scale;
    this.#translate.y = this.#startTranslate.y + dy / this.#scale;
    this.#requestUpdateTransform();
  }

  #handleTouchEnd = (event) => {
    if (event.touches.length === 0) {
      this.#isDragging = false;
      this.#requestUpdateTransform();
      this.#hasDraggedBeyondThreshold = false;
    }
  };

  #constrainTranslation() {
    const containerWidth = this.clientWidth;
    const containerHeight = this.clientHeight;
    if (!containerWidth || !containerHeight || !this.#image) return;
    this.#scale = clamp(this.#scale, MIN_ZOOM, MAX_ZOOM);
    if (this.#scale <= MIN_ZOOM) {
      this.#translate.x = 0;
      this.#translate.y = 0;
      return;
    }
    const wrapperRect = this.getBoundingClientRect();
    const imageElement = this.#image;
    let naturalWidth, naturalHeight;
    if (imageElement.naturalWidth > 0 && imageElement.naturalHeight > 0) {
      naturalWidth = imageElement.naturalWidth;
      naturalHeight = imageElement.naturalHeight;
    } else {
      naturalWidth = wrapperRect.width;
      naturalHeight = wrapperRect.width;
    }
    const imageAspectRatio = naturalWidth / naturalHeight;
    const wrapperAspectRatio = wrapperRect.width / wrapperRect.height;
    let actualImageWidth, actualImageHeight;
    if (imageAspectRatio > wrapperAspectRatio) {
      actualImageWidth = wrapperRect.width;
      actualImageHeight = actualImageWidth / imageAspectRatio;
    } else {
      actualImageHeight = wrapperRect.height;
      actualImageWidth = actualImageHeight * imageAspectRatio;
    }
    const scaledImageWidth = actualImageWidth * this.#scale;
    const scaledImageHeight = actualImageHeight * this.#scale;
    const horizontalOverflow = Math.max(0, scaledImageWidth - wrapperRect.width);
    const verticalOverflow = Math.max(0, scaledImageHeight - wrapperRect.height);
    const maxTranslateX = horizontalOverflow / 2 / this.#scale;
    const maxTranslateY = verticalOverflow / 2 / this.#scale;
    this.#translate.x = clamp(this.#translate.x, -maxTranslateX, maxTranslateX);
    this.#translate.y = clamp(this.#translate.y, -maxTranslateY, maxTranslateY);
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
  }

  #requestUpdateTransform = () => {
    if (!this.#animationFrame) this.#animationFrame = requestAnimationFrame(this.#updateTransform);
  };

  #cancelAnimationFrame() {
    if (this.#animationFrame) {
      cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = null;
    }
  }

  #updateTransform = () => {
    this.#animationFrame = null;
    this.#constrainTranslation();
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
    // Permitir swipe horizontal al slideshow cuando zoom = 1x
    this.style.touchAction = this.#scale <= MIN_ZOOM ? 'pan-x' : 'none';
  };

  #resetZoom = () => {
    this.#scale = DEFAULT_ZOOM;
    this.#startScale = DEFAULT_ZOOM;
    this.#translate.x = 0;
    this.#translate.y = 0;
    this.#startPosition = { x: 0, y: 0 };
    this.#startTranslate = { x: 0, y: 0 };
    this.#isDragging = false;
    this.#lastTapTime = 0;
    this.#lastTapPosition = null;
    this.#hasDraggedBeyondThreshold = false;
    this.style.setProperty('--drag-zoom-scale', DEFAULT_ZOOM.toString());
    this.style.setProperty('--drag-zoom-translate-x', '0px');
    this.style.setProperty('--drag-zoom-translate-y', '0px');
    this.style.touchAction = 'pan-x';
  };

  destroy() {
    this.#controller.abort();
    this.#cancelAnimationFrame();
  }
}

function getDistance(point1, point2) {
  const x1 = point1.x ?? point1.clientX;
  const y1 = point1.y ?? point1.clientY;
  const x2 = point2.x ?? point2.clientX;
  const y2 = point2.y ?? point2.clientY;
  const dx = x1 - x2;
  const dy = y1 - y2;
  return Math.sqrt(dx * dx + dy * dy);
}

if (!customElements.get('drag-zoom-wrapper')) {
  customElements.define('drag-zoom-wrapper', DragZoomWrapper);
}

/**
 * @typedef {Object} Point
 * @property {number} x
 * @property {number} y
 */

/**
 * @typedef {HTMLElement} ZoomDialogElement
 * @property {Function} close - Method to close the zoom dialog
 */
